import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Plus, BookOpen, Loader2 } from 'lucide-react'
import { useCreateCourse, useAddDeckToCourse, useAddFolderToCourse, useCourses } from '../hooks/useCourses'
import { extractErrorMessage } from '../api/client'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import type { Course } from '../types'

type Path = 'pick' | 'new' | 'existing'

interface Props {
  // At least one of deckIds/folderIds must be non-empty.
  deckIds?: string[]
  deckTitles?: string[]
  folderIds?: string[]
  folderTitles?: string[]
  onClose: () => void
  onSuccess: () => void
}

export default function GroupIntoCourseSheet({
  deckIds = [],
  deckTitles = [],
  folderIds = [],
  folderTitles = [],
  onClose,
  onSuccess,
}: Props) {
  const navigate = useNavigate()
  const [path, setPath] = useState<Path>('pick')
  const [newTitle, setNewTitle] = useState('')
  const [courseSearch, setCourseSearch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [successCourse, setSuccessCourse] = useState<{ id: string; title: string; addedCount: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const titleRef = useRef<HTMLInputElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  // ADR-106 P3: mounted only while open (from Mine's select mode, or from the
  // folder management modal, whose own block it nests inside).
  useSwipeNavBlock(true, 'group-into-course-sheet')

  const createCourse = useCreateCourse()
  const addDeck = useAddDeckToCourse()
  const addFolder = useAddFolderToCourse()
  const { data: coursesData } = useCourses(1, 100)

  const courses = (coursesData?.items ?? []).filter((c) =>
    !courseSearch || c.title.toLowerCase().includes(courseSearch.toLowerCase()),
  )

  // Total items being added (for UI copy)
  const totalItems = deckIds.length + folderIds.length

  // All item titles/icons for the summary strip
  const allItems: { title: string; icon: string }[] = [
    ...deckTitles.map((t) => ({ title: t, icon: '🃏' })),
    ...folderTitles.map((t) => ({ title: t, icon: '📁' })),
  ]

  useEffect(() => {
    if (path === 'new') titleRef.current?.focus()
  }, [path])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function addItemsBatch(courseId: string): Promise<number> {
    let added = 0
    for (const deckId of deckIds) {
      try {
        await addDeck.mutateAsync({ courseId, deckId })
        added++
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        // 409 = already in course — skip idempotently
        if (status === 409 || status === 422) { /* skip */ } else { throw err }
      }
    }
    for (const folderId of folderIds) {
      try {
        await addFolder.mutateAsync({ courseId, folderId })
        added++
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 409 || status === 422) { /* skip */ } else { throw err }
      }
    }
    return added
  }

  async function handleCreateNew() {
    const title = newTitle.trim()
    if (!title) return
    setError(null)
    setBusy(true)
    try {
      const course = await createCourse.mutateAsync({ title })
      const added = await addItemsBatch(course.id)
      setSuccessCourse({ id: course.id, title: course.title, addedCount: added })
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to create course. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleAddToExisting(course: Course) {
    setError(null)
    setBusy(true)
    try {
      const added = await addItemsBatch(course.id)
      setSuccessCourse({ id: course.id, title: course.title, addedCount: added })
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to add to course. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  function handleDone() {
    onSuccess()
    onClose()
  }

  const sheetStyle: React.CSSProperties = {
    position: 'fixed',
    left: 0, right: 0, bottom: 0,
    zIndex: 200,
    background: 'var(--bg-surface)',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
  }

  // ── Success state ────────────────────────────────────────────────────────────
  if (successCourse) {
    return (
      <>
        <div ref={backdropRef} onClick={handleDone} style={{ position: 'fixed', inset: 0, zIndex: 199, background: 'rgba(0,0,0,0.4)' }} />
        <div style={sheetStyle} role="dialog" aria-modal="true">
          <div style={{ padding: '28px 24px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
              Course updated
            </h2>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: 'var(--text-secondary)' }}>
              {successCourse.addedCount} item{successCourse.addedCount !== 1 ? 's' : ''} added to <strong>{successCourse.title}</strong>.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate(`/courses/${successCourse.id}`)}
                style={{ padding: '10px 22px', background: 'var(--accent-fill)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                Open course
              </button>
              <button
                onClick={handleDone}
                style={{ padding: '10px 22px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div ref={backdropRef} onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 199, background: 'rgba(0,0,0,0.4)' }} />
      <div style={sheetStyle} role="dialog" aria-modal="true" aria-label="Group into course">
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)' }} />
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px 8px', flexShrink: 0 }}>
          <div>
            {path !== 'pick' && (
              <button
                onClick={() => { setPath('pick'); setError(null) }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 14, fontWeight: 500, padding: 0, marginBottom: 2 }}
              >
                ← Back
              </button>
            )}
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
              {path === 'pick' && 'Group into course'}
              {path === 'new' && 'New course'}
              {path === 'existing' && 'Add to course'}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              {totalItems} item{totalItems !== 1 ? 's' : ''} selected
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Item summary strip */}
        <div style={{ padding: '0 20px 12px', overflowX: 'auto', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, minWidth: 'max-content' }}>
            {allItems.slice(0, 6).map((item, i) => (
              <span key={i} style={{ padding: '3px 10px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                {item.icon} {item.title.length > 18 ? item.title.slice(0, 17) + '…' : item.title}
              </span>
            ))}
            {allItems.length > 6 && (
              <span style={{ padding: '3px 10px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', fontSize: 12, color: 'var(--text-secondary)' }}>
                +{allItems.length - 6} more
              </span>
            )}
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', flexShrink: 0 }} />

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px 32px' }}>
          {error && (
            <div style={{ padding: '10px 14px', background: 'color-mix(in srgb, var(--danger) 12%, transparent)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {/* Path picker */}
          {path === 'pick' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => setPath('new')} style={pathCardStyle}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--accent) 12%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Plus size={20} color="var(--accent)" />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Create new course</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Group selected items into a brand-new course</div>
                </div>
              </button>
              <button
                onClick={() => setPath('existing')}
                style={pathCardStyle}
                disabled={!coursesData || (courses.length === 0 && !courseSearch)}
              >
                <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: 'color-mix(in srgb, var(--success) 12%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <BookOpen size={20} color="var(--success)" />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>Add to existing course</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {coursesData
                      ? coursesData.total === 0
                        ? 'No courses yet — create one first'
                        : `Choose from ${coursesData.total} course${coursesData.total !== 1 ? 's' : ''}`
                      : 'Loading courses…'}
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* New course form */}
          {path === 'new' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Course title
                </label>
                <input
                  ref={titleRef}
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newTitle.trim()) handleCreateNew() }}
                  placeholder="e.g. French Vocabulary B2"
                  style={{ width: '100%', padding: '10px 14px', fontSize: 15, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                />
              </div>
              <button
                onClick={handleCreateNew}
                disabled={!newTitle.trim() || busy}
                style={{
                  padding: '12px', background: newTitle.trim() && !busy ? 'var(--accent-fill)' : 'var(--bg-secondary)',
                  color: newTitle.trim() && !busy ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                  border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 600,
                  cursor: newTitle.trim() && !busy ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background 0.15s',
                }}
              >
                {busy
                  ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Creating…</>
                  : `Create course with ${totalItems} item${totalItems !== 1 ? 's' : ''}`}
              </button>
            </div>
          )}

          {/* Existing course list */}
          {path === 'existing' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                value={courseSearch}
                onChange={(e) => setCourseSearch(e.target.value)}
                placeholder="Search courses…"
                autoFocus
                style={{ width: '100%', padding: '9px 14px', fontSize: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              />
              {courses.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14, margin: '24px 0' }}>
                  {courseSearch ? `No courses match "${courseSearch}"` : 'No courses found.'}
                </p>
              ) : (
                courses.map((course) => (
                  <button
                    key={course.id}
                    onClick={() => handleAddToExisting(course)}
                    disabled={busy}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                      background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)', cursor: busy ? 'not-allowed' : 'pointer',
                      width: '100%', textAlign: 'left', opacity: busy ? 0.6 : 1,
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={(e) => { if (!busy) e.currentTarget.style.borderColor = 'var(--accent)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)' }}
                  >
                    <span style={{ fontSize: 20, flexShrink: 0 }}>📚</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{course.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {course.deck_count} deck{course.deck_count !== 1 ? 's' : ''}
                        {course.folder_count > 0 ? ` · ${course.folder_count} folder${course.folder_count !== 1 ? 's' : ''}` : ''}
                      </div>
                    </div>
                    {busy
                      ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-secondary)', flexShrink: 0 }} />
                      : <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, flexShrink: 0 }}>Add →</span>
                    }
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
      `}</style>
    </>
  )
}

const pathCardStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
  background: 'var(--bg-secondary)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)', cursor: 'pointer', width: '100%',
  textAlign: 'left', transition: 'border-color 0.15s, box-shadow 0.15s',
}
